import React, { useEffect, useMemo, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "configs/AppConfig";
import classes from "./Securitydeposit.module.css";

const cryptoPaymentOptions = {
  tron: {
    chain: "Tron Chain",
    token: "USDT TRC20",
    walletKey: "TRON_TRC20",
  },
  bnb: {
    chain: "BNB Chain",
    token: "USDT BEP20",
    walletKey: "BNB_BEP20",
  },
  ethereum: {
    chain: "Ethereum Chain",
    token: "USDT ERC20",
    walletKey: "ETHEREUM_ERC20",
  },
};

const cashLocation = {
  title: "Sovereign Auction payment desk",
  address: "Sovereign Auction Office, Dubai, United Arab Emirates",
  hours: "Monday to Saturday, 9:00 AM to 6:00 PM",
  note: "Please carry your Sovereign Auction ID and payment receipt details.",
};

function SecurityDepisitModal({ onClose }) {
  const { user } = useSelector((state) => state.user);
  const [selectedMethod, setSelectedMethod] = useState("");
  const [cryptoStep, setCryptoStep] = useState(1);
  const [amount, setAmount] = useState("");
  const [senderWalletAddress, setSenderWalletAddress] = useState("");
  const [paymentOption, setPaymentOption] = useState("tron");
  const [walletNetworks, setWalletNetworks] = useState({});
  const [walletsAreLoading, setWalletsAreLoading] = useState(false);
  const [walletsError, setWalletsError] = useState("");
  const [submitIsLoading, setSubmitIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  useEffect(() => {
    const fetchWalletNetworks = async () => {
      try {
        setWalletsAreLoading(true);
        setWalletsError("");
        const response = await fetch(`${API_BASE_URL}constant/wallet`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.message || "Failed to load wallet addresses.");
        }

        setWalletNetworks(data?.data?.CryptoWalletNetwork || {});
      } catch (error) {
        setWalletsError(error.message || "Failed to load wallet addresses.");
      } finally {
        setWalletsAreLoading(false);
      }
    };

    fetchWalletNetworks();
  }, []);

  const selectedCryptoOption = useMemo(
    () => {
      const option = cryptoPaymentOptions[paymentOption];

      return {
        ...option,
        walletAddress: walletNetworks[option.walletKey] || "",
      };
    },
    [paymentOption, walletNetworks]
  );

  const openMethod = (method) => {
    setSelectedMethod(method);
    setCryptoStep(1);
  };

  const goBack = () => {
    if (selectedMethod === "crypto" && cryptoStep === 2) {
      setCryptoStep(1);
      return;
    }

    if (selectedMethod) {
      setSelectedMethod("");
      return;
    }

    onClose();
  };

  const continueCryptoPayment = (event) => {
    event.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");
    setCryptoStep(2);
  };

  const submitCryptoSecurityDeposit = async (event) => {
    event.preventDefault();

    if (!user?.sessionToken) {
      setSubmitError("Please sign in before submitting your crypto deposit.");
      return;
    }

    try {
      setSubmitIsLoading(true);
      setSubmitError("");
      setSubmitSuccess("");

      const response = await fetch(`${API_BASE_URL}deposit/crypto`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.sessionToken}`,
        },
        body: JSON.stringify({
          amount: Number(amount),
          fromWalletAddress: senderWalletAddress,
          network: selectedCryptoOption.walletKey,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to submit crypto deposit.");
      }

      setSubmitSuccess(
        "Crypto security deposit submitted successfully. Our team will verify the transaction."
      );
    } catch (error) {
      setSubmitError(error.message || "Failed to submit crypto deposit.");
    } finally {
      setSubmitIsLoading(false);
    }
  };

  return (
    <main className={classes.page}>
      <section className={classes.shell}>
        <header className={classes.header}>
          <button type="button" className={classes.backButton} onClick={goBack}>
            Back
          </button>
          <div>
            <p className={classes.eyebrow}>Auction wallet</p>
            <h1>Add Security Deposit</h1>
          </div>
          <button type="button" className={classes.closeButton} onClick={onClose}>
            Close
          </button>
        </header>

        {!selectedMethod && (
          <div className={classes.methodGrid}>
            <button
              type="button"
              className={`${classes.methodCard} ${classes.disabledCard}`}
              disabled
            >
              <span>Online Payment</span>
              <strong>Coming soon</strong>
            </button>
            <button
              type="button"
              className={classes.methodCard}
              onClick={() => openMethod("crypto")}
            >
              <span>Crypto Payment</span>
              <strong>USDT only</strong>
            </button>
            <button
              type="button"
              className={classes.methodCard}
              onClick={() => openMethod("cash")}
            >
              <span>Cash</span>
              <strong>View location</strong>
            </button>
          </div>
        )}

        {selectedMethod === "cash" && (
          <section className={classes.contentPanel}>
            <p className={classes.panelLabel}>Cash deposit location</p>
            <h2>{cashLocation.title}</h2>
            <div className={classes.detailList}>
              <p>
                <span>Address</span>
                {cashLocation.address}
              </p>
              <p>
                <span>Working hours</span>
                {cashLocation.hours}
              </p>
              <p>
                <span>Note</span>
                {cashLocation.note}
              </p>
            </div>
          </section>
        )}

        {selectedMethod === "crypto" && cryptoStep === 1 && (
          <form className={classes.contentPanel} onSubmit={continueCryptoPayment}>
            <p className={classes.panelLabel}>Step 1 of 2</p>
            <h2>Crypto payment details</h2>
            <label className={classes.fieldLabel} htmlFor="depositAmount">
              Security deposit amount
            </label>
            <input
              id="depositAmount"
              className={classes.textInput}
              type="number"
              min="1"
              step="any"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="Enter deposit amount"
              required
            />
            <label className={classes.fieldLabel} htmlFor="senderWalletAddress">
              Wallet address you will send from
            </label>
            <input
              id="senderWalletAddress"
              className={classes.textInput}
              type="text"
              value={senderWalletAddress}
              onChange={(event) => setSenderWalletAddress(event.target.value)}
              placeholder="Enter your sending wallet address"
              required
            />
            <div className={classes.twoColumn}>
              <label className={classes.fieldLabel}>
                Payment route
                <select
                  className={classes.selectInput}
                  value={paymentOption}
                  onChange={(event) => setPaymentOption(event.target.value)}
                >
                  <option value="tron">Tron Chain - USDT TRC20</option>
                  <option value="bnb">BNB Chain - USDT BEP20</option>
                  <option value="ethereum">Ethereum Chain - USDT ERC20</option>
                </select>
              </label>
            </div>
            <button
              type="submit"
              className={classes.primaryButton}
              disabled={walletsAreLoading || !!walletsError}
            >
              {walletsAreLoading ? "Loading wallet..." : "Continue"}
            </button>
            {walletsError && (
              <p className={classes.errorText}>{walletsError}</p>
            )}
          </form>
        )}

        {selectedMethod === "crypto" && cryptoStep === 2 && (
          <form className={classes.contentPanel} onSubmit={submitCryptoSecurityDeposit}>
            <p className={classes.panelLabel}>Step 2 of 2</p>
            <h2>
              Send only {selectedCryptoOption.token} on{" "}
              {selectedCryptoOption.chain}
            </h2>
            <p className={classes.mutedText}>
              Complete the transfer first. After the transaction is done, click
              Submit Done so our team can verify it.
            </p>
            {walletsAreLoading && (
              <p className={classes.mutedText}>Loading receiving wallet...</p>
            )}
            {walletsError && (
              <p className={classes.errorText}>{walletsError}</p>
            )}
            <div className={classes.cryptoLayout}>
              <div className={classes.qrCode} aria-label="Wallet QR code">
                {selectedCryptoOption.walletAddress ? (
                  <QRCodeCanvas
                    value={selectedCryptoOption.walletAddress}
                    size={176}
                    level="M"
                    includeMargin
                  />
                ) : (
                  <p className={classes.mutedText}>Wallet not available</p>
                )}
              </div>
              <div className={classes.walletBox}>
                <span>Sovereign Auction receiving wallet</span>
                <strong>{selectedCryptoOption.walletAddress}</strong>
                <p>
                  Sending through a different chain or token standard can
                  permanently lose the funds. Use the exact wallet address shown
                  here.
                </p>
              </div>
            </div>
            <div className={classes.detailList}>
              <p>
                <span>Amount</span>
                {amount}
              </p>
              <p>
                <span>Your sending wallet</span>
                {senderWalletAddress}
              </p>
              <p>
                <span>Selected chain</span>
                {selectedCryptoOption.chain}
              </p>
              <p>
                <span>Selected token</span>
                {selectedCryptoOption.token}
              </p>
            </div>
            <button
              type="submit"
              className={classes.primaryButton}
              disabled={
                submitIsLoading ||
                walletsAreLoading ||
                !!walletsError ||
                !selectedCryptoOption.walletAddress
              }
            >
              {submitIsLoading ? "Submitting..." : "Submit Done"}
            </button>
            {submitError && <p className={classes.errorText}>{submitError}</p>}
            {submitSuccess && (
              <p className={classes.successText}>{submitSuccess}</p>
            )}
          </form>
        )}
      </section>
    </main>
  );
}

export default SecurityDepisitModal;
